/**
 * Converting between an instant and a wall-clock reading in a named zone.
 *
 * Rule 4: everything is stored UTC and rendered in the event's timezone, which
 * is a property of the event and not of whoever is looking. An organiser in
 * Spain configuring a Yorkshire event must see Yorkshire times, and a crew
 * phone that has been carried across a border must not silently shift.
 *
 * The browser's own local zone is deliberately never consulted here.
 *
 * `Intl` is part of the language, not a dependency, so this does not breach the
 * package's zero-runtime-dependency rule. There is no I/O.
 */
/**
 * True if the runtime recognises this as a real IANA zone.
 *
 * Worth doing rather than pattern-matching for a slash: `Europe/Londn` and
 * `GMT+1` both look plausible and both render every time in the wrong place,
 * which is the kind of fault nobody notices until a handover at 3am.
 *
 * It lives here rather than beside the event because every function below
 * depends on it. `Intl` throws a RangeError on an unknown zone, and a form
 * where someone is part-way through typing a zone name holds an unknown zone
 * on almost every keystroke — so this is a routine state, not an exceptional
 * one, and nothing here may throw on it.
 */
export function isKnownTimezone(timezone) {
    if (timezone.trim().length === 0)
        return false;
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: timezone });
        return true;
    }
    catch {
        return false;
    }
}
const FORMATTER_OPTIONS = {
    // h23, not `hour12: false`. Both give a 24-hour clock, but hour12:false
    // leaves the engine to pick between h23 and h24, and h24 renders midnight as
    // "24" — which rolls the reading onto the wrong day. Naming the cycle makes
    // the behaviour specified rather than defended against.
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
};
/**
 * The zone's offset from UTC at a given instant, in milliseconds.
 *
 * Found by formatting the instant in the zone and reading the result back as
 * though it were UTC; the difference is the offset. This is the standard trick
 * and it is correct across DST because it asks about one specific instant
 * rather than about the zone in general.
 */
function offsetMsAt(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone, ...FORMATTER_OPTIONS })
        .formatToParts(instant)
        .reduce((acc, part) => {
        if (part.type !== 'literal')
            acc[part.type] = Number(part.value);
        return acc;
    }, {});
    const asIfUtc = Date.UTC(parts['year'] ?? 1970, (parts['month'] ?? 1) - 1, parts['day'] ?? 1, parts['hour'] ?? 0, parts['minute'] ?? 0, parts['second'] ?? 0);
    return asIfUtc - instant.getTime();
}
/**
 * What a clock on the wall in `timeZone` reads at this instant.
 *
 * An unknown zone yields a reading of NaN fields rather than throwing. The
 * callers are render paths, and a thrown RangeError there blanks the whole
 * screen — which is exactly what it did the first time this met a half-typed
 * zone name.
 */
export function toWallClock(instant, timeZone) {
    if (!isKnownTimezone(timeZone)) {
        return { year: Number.NaN, month: Number.NaN, day: Number.NaN, hour: Number.NaN, minute: Number.NaN };
    }
    const shifted = new Date(instant.getTime() + offsetMsAt(instant, timeZone));
    return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
        hour: shifted.getUTCHours(),
        minute: shifted.getUTCMinutes(),
    };
}
const DAY_MS = 24 * 60 * 60 * 1000;
function sameWallClock(a, b) {
    return (a.year === b.year &&
        a.month === b.month &&
        a.day === b.day &&
        a.hour === b.hour &&
        a.minute === b.minute);
}
/**
 * The instant at which a wall clock in `timeZone` reads this.
 *
 * The offset depends on the instant, and the instant is what is being solved
 * for. Iterating towards it does not work: at a DST boundary the iteration
 * converges on one answer and cannot tell that it converged on the wrong one,
 * because a reading in the repeated hour is genuinely satisfied by two
 * different instants and both are fixed points.
 *
 * So take the offsets on either side of any possible transition — a day before
 * and a day after, which is far more than any transition needs — and keep the
 * candidates that actually read back as the requested wall clock.
 *
 * - **Two survive** for the hour repeated when clocks go back. The earlier is
 *   returned: it is the first time the clock reads this, and for anything
 *   behaving like a cut-off the earlier instant is the safer one.
 * - **None survive** for the hour skipped when clocks go forward, because that
 *   reading never happens. The result is shifted forward past the gap rather
 *   than being an error, which is what every other date library does and what
 *   an operator typing 01:30 on a spring Sunday means.
 */
export function fromWallClock(wall, timeZone) {
    if (!isKnownTimezone(timeZone))
        return new Date(Number.NaN);
    const naive = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
    const offsetBefore = offsetMsAt(new Date(naive - DAY_MS), timeZone);
    const offsetAfter = offsetMsAt(new Date(naive + DAY_MS), timeZone);
    const candidates = [...new Set([naive - offsetBefore, naive - offsetAfter])];
    const valid = candidates.filter((ms) => sameWallClock(toWallClock(new Date(ms), timeZone), wall));
    // No valid candidate means the reading falls in a gap. Using the offset from
    // before the transition lands just after it — the forward shift.
    if (valid.length === 0)
        return new Date(naive - offsetBefore);
    return new Date(Math.min(...valid));
}
const pad = (value, width = 2) => String(value).padStart(width, '0');
/**
 * The `YYYY-MM-DDTHH:mm` an `<input type="datetime-local">` expects, showing
 * the event's wall clock rather than the browser's.
 */
export function toDateTimeInput(instant, timeZone) {
    if (Number.isNaN(instant.getTime()) || !isKnownTimezone(timeZone))
        return '';
    const w = toWallClock(instant, timeZone);
    return `${pad(w.year, 4)}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
}
/**
 * The inverse. Returns an Invalid Date for anything unparseable rather than
 * guessing, so `validateEvent` is what reports it — one place that decides
 * whether a date is acceptable, not two.
 */
export function fromDateTimeInput(value, timeZone) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
    if (match === null)
        return new Date(Number.NaN);
    const [, year, month, day, hour, minute] = match;
    return fromWallClock({
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
        minute: Number(minute),
    }, timeZone);
}
/** For display: `13 Mar 2027, 08:00` in the event's zone. Never the viewer's. */
export function formatInZone(instant, timeZone) {
    if (Number.isNaN(instant.getTime()) || !isKnownTimezone(timeZone))
        return '—';
    return new Intl.DateTimeFormat('en-GB', {
        timeZone,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(instant);
}
/**
 * A small, honest fallback for runtimes without `Intl.supportedValuesOf`.
 *
 * Not an attempt at a world list — that is what the runtime is for. These are
 * the zones a UK-run endurance event plausibly needs, so an old browser gets a
 * usable picker rather than an empty one. Design rule 8 says assume the worst
 * device; this is what that costs here.
 */
const FALLBACK_TIMEZONES = [
    'Europe/London',
    'Europe/Dublin',
    'Europe/Paris',
    'Europe/Madrid',
    'Europe/Berlin',
    'Europe/Lisbon',
    'Atlantic/Canary',
    'UTC',
];
/**
 * Every timezone this runtime recognises, plus `UTC`.
 *
 * `Intl.supportedValuesOf('timeZone')` omits `UTC` even though `Intl` accepts
 * it, so it is added by hand — a picker without it would be quietly missing the
 * one zone anyone would look for first.
 */
export function supportedTimezones() {
    const supported = typeof Intl.supportedValuesOf === 'function'
        ? Intl.supportedValuesOf('timeZone')
        : FALLBACK_TIMEZONES;
    return [...new Set([...supported, 'UTC'])].sort((a, b) => a.localeCompare(b));
}
/**
 * The list a picker should offer, given what the event currently holds.
 *
 * `current` is always included even when the runtime does not recognise it.
 * A controlled `<select>` whose value is absent from its options renders
 * blank, and the next change would then save a zone nobody chose — so an
 * unrecognised stored value must stay visible and be corrected deliberately.
 * `validateEvent` is what flags it as wrong.
 */
export function timezoneOptions(current) {
    const zones = supportedTimezones();
    if (current.trim() === '' || zones.includes(current))
        return zones;
    return [...zones, current].sort((a, b) => a.localeCompare(b));
}
/**
 * Grouped by the part before the slash, for `<optgroup>`. 418 flat options is
 * a scroll; ten groups is a choice. `UTC` has no slash and gets its own group.
 */
export function groupTimezones(zones) {
    const groups = new Map();
    for (const zone of zones) {
        const region = zone.includes('/') ? (zone.split('/')[0] ?? zone) : 'Other';
        const bucket = groups.get(region);
        if (bucket === undefined)
            groups.set(region, [zone]);
        else
            bucket.push(zone);
    }
    return [...groups.entries()]
        .map(([region, list]) => ({ region, zones: list }))
        .sort((a, b) => a.region.localeCompare(b.region));
}
//# sourceMappingURL=time.js.map